import * as React from 'react';
import { Content, CTCParsedOutputModel, SectionWrapperModel } from '../../interfaces';
import renderDataTest from '../../utilities/testAutomation';
import ContentContext from '../App/Context';
import Button from '../Button/Button';
import InfoBox from '../InfoBox/InfoBox';
import NextStep from '../NextStep/NextStep';
import { ChevronPosition } from '../Icons/Chevron/Chevron';
import { formatInputModel } from '../../utilities/slider';

const Parser = require('html-react-parser');

interface Props {
  isSuccessful: boolean;
  hideOutputs: () => void;
  ctcOutput: CTCParsedOutputModel;
  inputModel: SectionWrapperModel;
}

interface State {
  guaranteedIncomeContainer?: HTMLDivElement;
  flexibleIncomeContainer?: HTMLDivElement;
}

const taxFreeSection = (text: string, sum: string) => (
  <div className="take-row">
    <p>{Parser(text)}</p>
    <span className="figure" {...renderDataTest('ppc-outputs-tax-free-lump-sum')}>{sum}</span>
  </div>
);

class OutputsBottom extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = { guaranteedIncomeContainer: undefined };
  }

  render() {
    const {
      inputModel: { neededMoney },
      isSuccessful,
      hideOutputs,
      ctcOutput,
    } = this.props;
    return (
      <ContentContext.Consumer>
        {(content: Content) => {
            const {
              analytics,
              outputs: {
                options,
                options: {
                  guaranteedincomebox,
                  flexibleIncomeBox,
                  cashBox,
                  combinationBox,
                  leaveItBox,
                },
                thingsToConsider,
                needAdvice,
              },
            } = content;

            return (
              <div className="grey-section">
                <div className="output-section">
                  {isSuccessful &&
                    <div>
                      <h3 className="intro-heading">{Parser(options.introHeading)}</h3>
                      <div className="output-assumptions">
                        <p>
                          {Parser(options.assumptions.text)}{' '}
                          <span className="output-assumptions--figure">{ctcOutput.ProjectedFund_Real}</span>
                        </p>
                      </div>
                      <div className="grid md--up income-options">
                        <InfoBox
                          heading={guaranteedincomebox.title}
                          linkText={guaranteedincomebox.linkText}
                          bodyText={
                            <div
                              className="body-text-content"
                              ref={(element: HTMLDivElement) => {
                                if (!this.state.guaranteedIncomeContainer) {
                                  this.setState({ guaranteedIncomeContainer: element });
                                }
                              }}
                            >
                              {Parser(guaranteedincomebox.bodyText1)}{' '}
                              {Parser(guaranteedincomebox.bodyText2)}
                            </div>
                          }
                        >
                          {taxFreeSection(options.taxfreelumpsum, ctcOutput.TFC)}
                          <div className="figure-row">
                            <p>{guaranteedincomebox.guaranteedincome}</p>
                            <span
                              className="figure"
                              {...renderDataTest('ppc-outputs-guaranteed-income')}
                            >
                              {ctcOutput.Annuity} {guaranteedincomebox.forLife}
                            </span>
                          </div>
                        </InfoBox>
                        <InfoBox
                          heading={flexibleIncomeBox.title}
                          linkText={flexibleIncomeBox.linkText}
                          bodyText={
                            <div
                              className="body-text-content"
                              ref={(element: HTMLDivElement) => {
                                if (!this.state.flexibleIncomeContainer) {
                                  this.setState({ flexibleIncomeContainer: element });
                                }
                              }}
                            >
                              {Parser(flexibleIncomeBox.bodyText2)}
                            </div>
                          }
                        >
                          {taxFreeSection(options.taxfreelumpsum, ctcOutput.TFC)}
                          <div className="figure-row">
                            <p>{flexibleIncomeBox.dipInto}</p>
                            <span
                              className="figure"
                              {...renderDataTest('ppc-outputs-pot-value-after-tax-free-lump-sum')}
                            >
                              {ctcOutput.ResidualFundBeforeTax}
                            </span>
                            <p>{flexibleIncomeBox.leavingTheRest}</p>
                            <p className="or">{Parser(options.or)}</p>
                            <p>{flexibleIncomeBox.takeAnnual}</p>
                            <div className="tooltip-wrapper">
                              <span
                                className="figure"
                                {...renderDataTest('ppc-outputs-annual-income')}
                              >
                                £{formatInputModel(neededMoney)}
                              </span>
                              <span className="horizontal-padding-small">
                                {Parser(flexibleIncomeBox.until)}{Number(ctcOutput.FundExhausionAge) === -1 ? 'at least' : ''}
                              </span>
                              <span
                                className="figure"
                                {...renderDataTest('ppc-outputs-annual-income-runs-out')}
                              >
                                {Number(ctcOutput.FundExhausionAge) === -1 ? 'age 119' :
                              `${Parser(flexibleIncomeBox.age)} ${ctcOutput.FundExhausionAge}`}
                              </span>
                            </div>
                          </div>
                        </InfoBox>
                        <InfoBox
                          heading={cashBox.title}
                          linkText={cashBox.linkText}
                        >
                          {taxFreeSection(options.taxfreelumpsum, ctcOutput.TFC)}
                          <div className="figure-row">
                            <p>{cashBox.remaining}</p>
                            <span
                              className="figure"
                              {...renderDataTest('ppc-outputs-lump-sum-after-tax')}
                            >
                              {ctcOutput.ResidualFundAfterTax}
                            </span>
                            <p>{cashBox.plusTaxFree}</p>
                            <br />
                            <p>{cashBox.incomeTax}</p>
                            <span
                              className="figure figure--burgundy"
                              {...renderDataTest('ppc-outputs-lump-sum-tax')}
                            >
                              {ctcOutput.TaxDue}
                            </span>
                            <p className="or">{Parser(options.or)}</p>
                            <p className="cash-info">{cashBox.cashInfo}</p>
                          </div>
                        </InfoBox>
                      </div>
                      <Button
                        name={options.backButton.label}
                        onClick={hideOutputs}
                        chevron={ChevronPosition.Left}
                        data-dtm-id="buttons"
                        data-dtm-data={analytics.button.edit}
                        {...renderDataTest('ppc-outputs-button-back')}
                      />
                    </div>}
                  <div className="grid md--up income-options no-border">
                    <InfoBox
                      heading={combinationBox.title}
                      bodyText={combinationBox.bodyText}
                      linkText={combinationBox.linkText}
                    />
                    <InfoBox
                      heading={leaveItBox.title}
                      bodyText={leaveItBox.bodyText}
                      linkText={leaveItBox.linkText}
                    />
                  </div>
                  <div className="need-advice-wrapper">
                    <div className="info-box">
                      <div className="need-advice">
                        <div className="heading-wrapper">
                          <div className="heading">
                            {needAdvice.heading}
                          </div>
                        </div>
                        <div className="body-text-wrapper">
                          <div>{Parser(needAdvice.bodyText)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="things-to-consider--wrapper">
                    <div className="info-box">
                      <div className="things-to-consider">
                        <div className="heading-wrapper">
                          <div className="heading">
                            {thingsToConsider.title}
                          </div>
                        </div>
                        <div className="body-text-wrapper">
                          <div>{Parser(thingsToConsider.bodyText)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <NextStep
                    dataTest={renderDataTest('ppc-outputs-customer-number')}
                    shouldBoxShow={isSuccessful}
                  />
                </div>
              </div>
            );
          }
        }
      </ContentContext.Consumer>
    );
  }
}

export default OutputsBottom;



// WEBPACK FOOTER //
// ./src/components/Outputs/OutputsBottom.tsx